import { Controller } from "@hotwired/stimulus"

/**
 * The properties body of a gen4 barrier on the floorplan
 * (`Floorplans::BarrierPropertiesComponent`).
 *
 * Renders state, and nothing else: the zone and name it shows are changed
 * through the canvas controller (`setBarrierZone`, `renameBarrier`), which
 * owns the canvas. No user-visible string is written here — every label is
 * rendered by the server — and no design-system class is named.
 */
export default class extends Controller {
  static targets = ["field", "zone", "name", "linkedHint"]

  /** data-action="barrier:state@document->floorplans--barrier#render" */
  render(event) {
    const state = event.detail
    // A click on the canvas does not blur the panel (CONEK-8680), so a name
    // still focused when the selection moves was typed for the barrier the
    // field shows: commit it for that one (`data-barrier-id`) before it goes.
    const shown = this.hasNameTarget
      ? this.nameTarget.dataset.barrierId
      : undefined
    const moved = (shown ?? null) !== (state?.id ?? null)
    if (
      moved &&
      this.hasNameTarget &&
      document.activeElement === this.nameTarget
    ) {
      this.nameTarget.dispatchEvent(new Event("change", { bubbles: true }))
    }
    if (!state) {
      if (this.hasNameTarget) delete this.nameTarget.dataset.barrierId
      return
    }

    const values = {
      product: state.productName,
      length: this.#metres(state.lengthMm),
      posts: String(state.posts),
      ends: String(state.postsByKind?.end ?? 0),
      mid_joints: String(state.postsByKind?.["mid-joint"] ?? 0),
      corners: String(state.postsByKind?.corner ?? 0),
      smartcaps: String(state.smartcaps),
    }
    for (const field of this.fieldTargets) {
      field.textContent = values[field.dataset.barrierField] ?? ""
    }

    // Not while somebody is typing in it: the answer to their own change
    // would otherwise move the cursor. Another barrier always shows its own.
    if (
      this.hasZoneTarget &&
      (moved || document.activeElement !== this.zoneTarget)
    ) {
      this.#setSelect(this.zoneTarget, state.zoneId ?? "")
    }
    if (this.hasNameTarget) {
      this.nameTarget.dataset.barrierId = state.id
      if (moved || document.activeElement !== this.nameTarget) {
        this.nameTarget.value = state.name ?? ""
      }
    }

    // A barrier registered in facilities takes its name and zone from there
    // on every Sync (CONEK-8870): shown, not offered, and the hint says where.
    const linked = Boolean(state.linked)
    if (this.hasNameTarget) this.nameTarget.disabled = linked
    if (this.hasZoneTarget) this.#lockSelect(this.zoneTarget, linked)
    if (this.hasLinkedHintTarget) this.linkedHintTarget.hidden = !linked
  }

  // Metres to one decimal, as the racking panel shows a run; the unit is in
  // the label.
  #metres(mm) {
    return new Intl.NumberFormat(document.documentElement.lang || undefined, {
      minimumFractionDigits: 1,
      maximumFractionDigits: 1,
    }).format(Number(mm) / 1000)
  }

  // A Tom Select-backed select is locked through Tom Select.
  #lockSelect(select, locked) {
    if (select.tomselect) {
      if (locked) select.tomselect.disable()
      else select.tomselect.enable()
    } else {
      select.disabled = locked
    }
  }

  // A Tom Select-backed select keeps its own value; set both.
  #setSelect(select, value) {
    if (select.tomselect) {
      select.tomselect.setValue(value, true)
    } else {
      select.value = value
    }
  }
}
